import { api } from './api';

export interface InvoiceTemplate {
  id: string;
  name: string;
  description?: string | null;
  companyId: string;
  templateCompanyName: string;
  templateLogo?: string | null;
  logoBase64?: string | null;
  layoutType: 'modern' | 'classic';
  colorScheme: {
    primary: string;
    secondary: string;
    text: string;
    background: string;
  };
  fontFamily: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTemplateData {
  name: string;
  description?: string;
  templateCompanyName: string;
  layoutType: 'modern' | 'classic';
  colorScheme?: {
    primary: string;
    secondary: string;
    text: string;
    background: string;
  };
  fontFamily?: string;
  isDefault?: boolean;
}

export interface UpdateTemplateData {
  name?: string;
  description?: string;
  templateCompanyName?: string;
  layoutType?: 'modern' | 'classic';
  colorScheme?: {
    primary: string;
    secondary: string;
    text: string;
    background: string;
  };
  fontFamily?: string;
  isDefault?: boolean;
  isActive?: boolean;
}

export const invoiceTemplateService = {
  // Get all templates
  getAll: async (includeInactive = false): Promise<InvoiceTemplate[]> => {
    const response = await api.get('/invoice-templates', {
      params: { includeInactive: includeInactive.toString() },
    });
    return response.data.data;
  },

  // Get single template by ID
  getById: async (id: string): Promise<InvoiceTemplate> => {
    const response = await api.get(`/invoice-templates/${id}`);
    return response.data.data;
  },

  // Create new template
  create: async (data: CreateTemplateData): Promise<InvoiceTemplate> => {
    const response = await api.post('/invoice-templates', data);
    return response.data.data;
  },

  // Update template
  update: async (id: string, data: UpdateTemplateData): Promise<InvoiceTemplate> => {
    const response = await api.put(`/invoice-templates/${id}`, data);
    return response.data.data;
  },

  // Delete template
  delete: async (id: string): Promise<void> => {
    await api.delete(`/invoice-templates/${id}`);
  },

  // Upload template logo
  uploadLogo: async (id: string, file: File): Promise<InvoiceTemplate> => {
    const formData = new FormData();
    formData.append('logo', file);

    const response = await api.post(`/invoice-templates/${id}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data.data;
  },

  // Delete template logo
  deleteLogo: async (id: string): Promise<InvoiceTemplate> => {
    const response = await api.delete(`/invoice-templates/${id}/logo`);
    return response.data.data;
  },

  // Set template as default
  setDefault: async (id: string): Promise<InvoiceTemplate> => {
    const response = await api.patch(`/invoice-templates/${id}/set-default`);
    return response.data.data;
  },

  // Get logo URL
  getLogoUrl: (logo: string | null | undefined): string | null => {
    if (!logo) return null;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5002/api';
    const baseUrl = apiUrl.replace('/api', '');
    return `${baseUrl}/${logo}`;
  },

  // Get default color schemes for layout types
  getDefaultColorScheme: (layoutType: 'modern' | 'classic') => {
    const schemes = {
      modern: {
        primary: '#2563EB',
        secondary: '#10B981',
        text: '#1F2937',
        background: '#FFFFFF',
      },
      classic: {
        primary: '#374151',
        secondary: '#6B7280',
        text: '#111827',
        background: '#F9FAFB',
      },
    };
    return schemes[layoutType];
  },
};
